import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { UserPlus, KeyRound, Pencil } from "lucide-react";

export default function AdminStaff() {
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ username: "", name: "", email: "", password: "" });
  const [resetFor, setResetFor] = useState(null);
  const [newPass, setNewPass] = useState("");
  const [editFor, setEditFor] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "" });
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/staff");
    setStaff(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post("/admin/staff", form);
      toast.success(`Staff account created for ${form.name}.`);
      setOpen(false);
      setForm({ username: "", name: "", email: "", password: "" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const toggleActive = async (s) => {
    try {
      await api.patch(`/admin/staff/${s.id}`, { active: !s.active });
      toast.success(`${s.name} ${!s.active ? "activated" : "deactivated"}.`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const doReset = async () => {
    try {
      await api.post(`/admin/staff/${resetFor.id}/reset-password`, { password: newPass });
      toast.success(`Password reset for ${resetFor.name}.`);
      setResetFor(null);
      setNewPass("");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const openEdit = (s) => { setEditForm({ name: s.name, email: s.email || "" }); setEditFor(s); };
  const doEdit = async () => {
    try {
      await api.patch(`/admin/staff/${editFor.id}/profile`, editForm);
      toast.success(`${editForm.name}'s details updated.`);
      setEditFor(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-staff">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-3xl font-bold">Staff Accounts</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-staff-btn"><UserPlus className="h-4 w-4 mr-2" /> Add Staff</Button>
          </DialogTrigger>
          <DialogContent data-testid="add-staff-dialog">
            <DialogHeader><DialogTitle>Create Staff Account</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2"><Label>Full name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name-input" /></div>
              <div className="space-y-2"><Label>Username</Label>
                <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="staff-username-input" /></div>
              <div className="space-y-2"><Label>Email (optional)</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email-input" /></div>
              <div className="space-y-2"><Label>Temporary password</Label>
                <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password-input" /></div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={!form.name || !form.username || !form.password} data-testid="staff-create-submit">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="rounded-xl border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Username</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="staff-rows">
            {staff.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No staff yet. Add your first team member.</TableCell></TableRow>}
            {staff.map((s) => (
              <TableRow key={s.id}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell>{s.username}</TableCell>
                <TableCell className={s.email ? "" : "text-muted-foreground italic"} data-testid={`staff-email-${s.id}`}>{s.email || "no email"}</TableCell>
                <TableCell>
                  <Switch checked={s.active} onCheckedChange={() => toggleActive(s)} data-testid={`toggle-active-${s.id}`} />
                </TableCell>
                <TableCell className="text-right space-x-2 whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`edit-staff-${s.id}`}>
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setResetFor(s)} data-testid={`reset-pass-${s.id}`}>
                    <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/admin/staff/${s.id}`)} data-testid={`view-${s.id}`}>View</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!editFor} onOpenChange={(o) => !o && setEditFor(null)}>
        <DialogContent data-testid="edit-staff-dialog">
          <DialogHeader><DialogTitle>Edit — {editFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Full name</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-staff-name" /></div>
            <div className="space-y-2"><Label>Email</Label>
              <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} placeholder="name@example.com — roster emails go here" data-testid="edit-staff-email" /></div>
          </div>
          <DialogFooter>
            <Button onClick={doEdit} disabled={!editForm.name} data-testid="edit-staff-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetFor} onOpenChange={(o) => !o && setResetFor(null)}>
        <DialogContent data-testid="reset-dialog">
          <DialogHeader><DialogTitle>Reset password — {resetFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>New password</Label>
            <Input value={newPass} onChange={(e) => setNewPass(e.target.value)} data-testid="reset-password-input" />
          </div>
          <DialogFooter>
            <Button onClick={doReset} disabled={!newPass} data-testid="reset-password-submit">Set Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
